import { format, formatDistanceToNowStrict, isToday, isYesterday } from 'date-fns';
import { parseDbDate } from './dates';

const numberFmt = new Intl.NumberFormat('en-IN');
const compactFmt = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

export const formatNumber = (n: number | null | undefined) => numberFmt.format(Number(n) || 0);

export const formatCompact = (n: number | null | undefined) => {
  const v = Number(n) || 0;
  return Math.abs(v) < 10_000 ? numberFmt.format(v) : compactFmt.format(v);
};

export function formatPercent(part: number, whole: number): string {
  if (!whole) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}

/** 3725 → "1h 2m", 185 → "3m 5s", 42 → "42s". */
export function formatDuration(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total === 0) return '0s';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return s ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

/** Media clock: 65 → "1:05". */
export function formatClock(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

export function formatBytes(bytes: number | null | undefined, decimals = 1): string {
  const value = Number(bytes) || 0;
  if (value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  const n = value / 1024 ** i;
  return `${n.toFixed(i === 0 ? 0 : decimals).replace(/\.0+$/, '')} ${units[i]}`;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const d = parseDbDate(value);
  return d ? format(d, 'd MMM yyyy, h:mm a') : '—';
}

export function formatDate(value: string | Date | null | undefined): string {
  const d = parseDbDate(value);
  return d ? format(d, 'd MMM yyyy') : '—';
}

export function formatTime(value: string | Date | null | undefined): string {
  const d = parseDbDate(value);
  return d ? format(d, 'h:mm a') : '—';
}

/** "Today", "Yesterday" or "12 Mar". */
export function formatDayLabel(value: string | Date | null | undefined): string {
  const d = parseDbDate(value);
  if (!d) return '—';
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, d.getFullYear() === new Date().getFullYear() ? 'd MMM' : 'd MMM yyyy');
}

export function formatRelative(value: string | Date | null | undefined): string {
  const d = parseDbDate(value);
  if (!d) return 'Never';
  if (Date.now() - d.getTime() < 45_000) return 'Just now';
  return `${formatDistanceToNowStrict(d)} ago`;
}

export function initials(name: string | null | undefined, fallback = '?'): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase() || fallback;
}

export const plural = (n: number, word: string, pluralWord = `${word}s`) =>
  `${formatNumber(n)} ${n === 1 ? word : pluralWord}`;
